import { FC } from 'react';
import { User } from '@carbon/react/icons';
import type { AuditSegment, AuditGateRow } from 'types/api-responses';
import { AuditSegments } from 'components/audit-segments';
import style from './audit-log.module.scss';

interface Props {
  user: string;
  action: string;
  description: string;
  // Optional structured detail for grouped reconcile / undo entries: one bullet
  // per line, entity segments rendered as inline "<name> (Entity ID: <id>)" links. Falls
  // back to `description` when absent.
  details?: AuditSegment[][];
  // Auto-confirm rows carry the full gate rundown; rendered as a table under the
  // sentence so an examiner can see every guardrail that cleared.
  gateTrace?: AuditGateRow[];
}

const GATE_RESULT_LABEL: Record<AuditGateRow['result'], string> = {
  pass: 'Pass',
  fail: 'Fail',
  disabled: 'Off',
  not_evaluated: 'N/A',
};

const GATE_CHIP_CLASS: Record<AuditGateRow['result'], string> = {
  pass: style.gateChipPass,
  fail: style.gateChipFail,
  disabled: style.gateChipDisabled,
  not_evaluated: style.gateChipDisabled,
};

// Expanded body for a human (or System) action row.
export const EventExpanded: FC<Props> = ({
  user,
  action,
  description,
  details,
  gateTrace,
}) => {
  const isSystem = user === 'System';
  return (
    <div className={style.expandedWrapper}>
      <div className={style.eventEntry}>
        <div
          className={`${style.eventIcon} ${
            isSystem ? style.eventIconSystem : ''
          }`}
        >
          <User size={16} />
        </div>
        <div>
          <p className={style.eventText}>
            <strong>{user}</strong> — {action.toLowerCase()}
          </p>
          {details && details.length > 0 ? (
            <ul className={style.eventDetailList}>
              {details.map((line, i) => (
                <li key={i} className={style.eventDetail}>
                  <AuditSegments segments={line} />
                </li>
              ))}
            </ul>
          ) : (
            <p className={style.eventDetail}>{description}</p>
          )}
          {gateTrace && gateTrace.length > 0 && (
            <table className={style.gateTrace}>
              <thead>
                <tr>
                  <th>Check</th>
                  <th>Requirement</th>
                  <th>Observed</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {gateTrace.map((g) => (
                  <tr
                    key={g.guardrailId}
                    className={g.result === 'fail' ? style.gateRowFail : ''}
                  >
                    <td className={style.gateId}>{g.guardrailId}</td>
                    <td>{g.requirement}</td>
                    <td className={style.gateObserved}>{g.observed}</td>
                    <td>
                      <span
                        className={`${style.gateChip} ${
                          GATE_CHIP_CLASS[g.result] ?? style.gateChipDisabled
                        }`}
                      >
                        {GATE_RESULT_LABEL[g.result] ?? g.result}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
